<template>
  <div class="fretboard-tooltip-content">
    <!-- Note name and position -->
    <div class="note-info">
      <span class="note-name">{{ data.noteName }}</span>
      <span v-if="getAlternateName(data.noteName)" class="alternate-name">
        {{ getAlternateName(data.noteName) }}
      </span>
    </div>

    <!-- Scale degree (if applicable) -->
    <div v-if="data.scaleDegree !== undefined" class="scale-degree">
      <span class="degree-value">{{
        formatScaleDegree(data.scaleDegree)
      }}</span>
      scale degree
    </div>

    <!-- Instruction (only shown if provided) -->
    <div v-if="data.instruction" class="instruction">
      {{ data.instruction }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps } from "vue";
import { getNoteEnharmonicInfo } from "@/utils/noteUtils";

interface TooltipData {
  noteName: string;
  scaleDegree?: string | number;
  instruction?: string;
  fret: number;
  string: number;
}

interface Props {
  data: TooltipData;
}

defineProps<Props>();

function formatScaleDegree(degree: string | number): string {
  if (typeof degree === "number") {
    // Convert 0-based to 1-based and add ordinal suffix
    const oneBasedDegree = degree + 1;
    const suffixes = ["st", "nd", "rd"];
    const suffix = suffixes[oneBasedDegree - 1] || "th";
    return `${oneBasedDegree}${suffix}`;
  }
  return degree.toString();
}

function getAlternateName(noteName: string): string | null {
  // Convert note name to chromatic interval, then get enharmonic info
  // This is a simple mapping - in practice, this would use the interval from the note data
  const noteToInterval: Record<string, number> = {
    C: 0,
    "C#": 1,
    Db: 1,
    D: 2,
    "D#": 3,
    Eb: 3,
    E: 4,
    F: 5,
    "F#": 6,
    Gb: 6,
    G: 7,
    "G#": 8,
    Ab: 8,
    A: 9,
    "A#": 10,
    Bb: 10,
    B: 11,
  };

  const interval = noteToInterval[noteName];
  if (interval === undefined) return null;

  const enharmonicInfo = getNoteEnharmonicInfo(interval);

  // Return the alternate (the one that's not the current primary)
  if (enharmonicInfo.primary === noteName) {
    return enharmonicInfo.alternate;
  } else if (enharmonicInfo.alternate === noteName) {
    return enharmonicInfo.primary;
  }

  return null;
}
</script>

<style scoped lang="scss">
.fretboard-tooltip-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: max-content;
  white-space: nowrap;
}

.note-info {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.note-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--shade-70);
}

.alternate-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--shade-50);
}

.scale-degree {
  font-size: 13px;
  color: var(--shade-70);
}

.degree-value {
  font-weight: 600;
  color: var(--shade-70);
}

.instruction {
  font-size: 12px;
  color: var(--shade-60);
  font-style: italic;
  border-top: 1px solid var(--shade-30);
  padding-top: 6px;
  margin-top: 2px;
}
</style>
